import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { LoginComponent } from './component/login/login.component';
import { NavComponent } from './component/nav/nav.component';
import { HomeComponent } from './component/home/home.component';
import { AdminListComponent } from './component/admin/admin-list/admin-list.component';
import { QrCodeListComponent } from './component/qr/qr-code-list/qr-code-list.component';
import { LuckyDrawListComponent } from './component/luckydraw/lucky-draw-list/lucky-draw-list.component';
import { CustomerRedeemListComponent } from './component/redeem/customer-redeem-list/customer-redeem-list.component';
import { QrHistoryComponent } from './component/qr/qr-history/qr-history.component';
import { QrIllegalclaimComponent } from './component/qr/qr-illegalclaim/qr-illegalclaim.component';
import { QrCodeDetailsComponent } from './component/qr/qr-code-details/qr-code-details.component';
import { RedeemItemsListComponent } from './component/redeem/redeem-items-list/redeem-items-list.component';
import { CustomerListComponent } from './component/customer/customer-list/customer-list.component';
import { CustomerInfoComponent } from './component/customer/customer-info/customer-info.component';
import { CustomerUnverifyListComponent } from './component/customer/customer-unverify-list/customer-unverify-list.component';
import { RedeemTransactionListComponent } from './component/redeem/redeem-transaction-list/redeem-transaction-list.component';
import { QrUploadComponent } from './component/qr/qr-upload/qr-upload.component';
import { TransactionComponent } from './component/transaction/transaction.component';
import { BlockPhoneComponent } from './component/block-phone/block-phone.component';
import { RedeemDownloadListComponent } from './component/redeem/redeem-download-list/redeem-download-list.component';
import { LmsCouponCodeList } from './component/lms/lms-coupon-code-list/lms-coupon-code-list';

// Custom security services dependencies imports
import { authGuard } from './utils/auth.guard'; 
import { RoleGuardService } from './utils/role-guard.service'; 
import { UserLoginRole } from './enumeration/roles';
import { LmsCourseItem } from './component/lms/lms-course-item/lms-course-item';
import { LmsCourseCatalog } from './component/lms/lms-course-catalog/lms-course-catalog';
import { LmsCourseRedeem } from './component/lms/lms-course-redeem/lms-course-redeem';

/**
 * Modern inline helper utility function to enforce role validation rules quickly
 */
const hasRole = (allowedRoles: string[]) => () => inject(RoleGuardService).checkRoleAccess(allowedRoles);

export const routes: Routes = [
  { path: 'login', component: LoginComponent },
  {
    path: 'nav',
    component: NavComponent,
    canActivate: [authGuard], // Verifies user is actively authenticated
    children: [
      { path: '', redirectTo: 'home', pathMatch: 'full' },
      
      // Home Workspace Module
      { 
        path: 'home', 
        component: HomeComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN, UserLoginRole.LUCKY_DRAW_ADMIN, UserLoginRole.REDEEM_PAYMENT_ADMIN, UserLoginRole.AUDIT_ADMIN, UserLoginRole.LMS_ADMIN])]
      },

      // Admin Governance Management Scope
      {
        path: 'admin-list',
        component: AdminListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN])]
      },

      // QR & Logistics Operational Assets Scope
      {
        path: 'qr-code-list',
        component: QrCodeListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN, UserLoginRole.LUCKY_DRAW_ADMIN, UserLoginRole.AUDIT_ADMIN])]
      },
      { path: 'qr-code-history/:phone', component: QrHistoryComponent },
      { path: 'qr-code-illegalclaim/:qrcode', component: QrIllegalclaimComponent },
      { path: 'qr-code-details/:qrcode', component: QrCodeDetailsComponent },
      {
        path: 'qr-upload',
        component: QrUploadComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN])]
      },

      // Lucky Draw Campaigns Scope
      {
        path: 'lucky-draw-list',
        component: LuckyDrawListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.LUCKY_DRAW_ADMIN])]
      },

      // Telecommunications & Rewards Redemptions Scope
      { 
        path: 'redeem-item-list', 
        component: RedeemItemsListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN])]
      },
      { path: 'redeem-transaction-list/:redeemId/:type', component: RedeemTransactionListComponent },
      { 
        path: 'customer-redeem-list/:phone', 
        component: CustomerRedeemListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN, UserLoginRole.REDEEM_PAYMENT_ADMIN])]
      },
      {
        path: 'redeem-download-list',
        component: RedeemDownloadListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN,UserLoginRole.ADMIN, UserLoginRole.REDEEM_PAYMENT_ADMIN, UserLoginRole.ACCOUNT_ADMIN])]
      },

      // Customer Profiles & Core System Ledger Auditing Operations Scope
      { 
        path: 'customer-list', 
        component: CustomerListComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN])]
      },
      { path: 'customer-info/:phone', component: CustomerInfoComponent },
      { path: 'customer-unverify-list', component: CustomerUnverifyListComponent },
      { 
        path: 'transaction-list', 
        component: TransactionComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN])]
      },
      { 
        path: 'block-phone-list', 
        component: BlockPhoneComponent,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.ADMIN])]
      },
      {
        path: 'lms-course-catalog',
        component: LmsCourseCatalog,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.LMS_ADMIN])]
      },
      {
        path: 'lms-course-item-list',
        component: LmsCourseItem,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.LMS_ADMIN])]
      },
      { 
        path: 'lms-coupon-code-list', 
        component: LmsCouponCodeList,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.LMS_ADMIN])]
      },
      {
        path: 'lms-course-redeem-list',
        component: LmsCourseRedeem,
        canActivate: [hasRole([UserLoginRole.SUPER_ADMIN, UserLoginRole.LMS_ADMIN])]
      },

      // Wildcard fallback redirect segment safety valve
      { path: '**', redirectTo: 'home', pathMatch: 'full' },
    ],
  },
  // Global catchall fallback rule 
  { path: '**', redirectTo: 'login', pathMatch: 'full' }
];
